/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * FeedbackView — Tier 2 AAR Feedback Loop coordinator UI.
 *
 * Spec: memory-bank/plans/tier2-feedback-loop.md §1 (Minimum viable loop)
 *       memory-bank/plans/tier2-feedback-loop-completion.md (session 5.12 scope)
 *
 * Features:
 *   - List AAR feedback entries with tag, reason, created_at, applied status
 *   - Filter by feedback tag, date range, and applied/pending status
 *   - Bulk-select rows and mark selected as applied (coordinator control)
 *   - Form to create new feedback (select AAR, pick tag, enter reason)
 *   - RBAC: coordinators and above only
 *
 * Layout:
 *   [H1: AAR Feedback] [+ New Feedback button]
 *   [Banner: pending vs applied explainer]
 *   [FilterBar: search, tag, date range]
 *   [Pending-only toggle + Mark selected applied (when selection non-empty)]
 *   [Active filter chips + Clear all]
 *   [DataTable: checkbox | Tag | Reason | Created | Applied]
 *   [Modal: New Feedback form]
 *
 * URL-synced, server-side paginated (50 per page). Coordinator+ only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import type { UserRole } from '@/types';
import { useListQuery } from '@/hooks/useListQuery';
import DataTable from '@/components/DataTable';
import EmptyState from '@/components/EmptyState';
import {
  FilterBar,
  SearchInput,
  FilterSelect,
  FilterDateRange,
} from '@/components/filters';
import {
  AARFeedback,
  bulkApplyFeedback,
  submitAARFeedback,
  FEEDBACK_TAG_OPTIONS,
  getFeedbackTagLabel,
  getFeedbackTagColor,
} from '@/api/feedback';
import client from '@/api/client';

interface AAROption {
  value: string;
  label: string;
}

/**
 * FeedbackView — Coordinator UI for AAR feedback tagging and tracking.
 * Coordinator+ only.
 */
export function FeedbackView({ userRole }: { userRole?: UserRole }) {
  // ============================================================================
  // Access control
  // ============================================================================
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  const isCoordinator = ['COORDINATOR', 'MANAGER', 'ORG_MANAGER', 'ADMIN'].includes(
    roleNorm
  );

  if (!isCoordinator) {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">Access Denied</h2>
          <p className="text-fg-secondary">
            The AAR Feedback view is available to coordinators and above only.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // State for dropdowns, modal, and bulk selection
  // ============================================================================
  const [aarOptions, setAarOptions] = useState<AAROption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [showNewFeedbackModal, setShowNewFeedbackModal] = useState(false);

  // Bulk selection set — resets when the page/filter changes.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);

  // Modal form state
  const [formAarId, setFormAarId] = useState<string | null>(null);
  const [formTag, setFormTag] = useState<string | null>(null);
  const [formReason, setFormReason] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);

  // ============================================================================
  // Query hook: server-side list query for feedback
  // ============================================================================
  const query = useListQuery<AARFeedback>({
    endpoint: '/canvass/aars/feedback',
    pageSize: 50,
  });

  // Clear the selection whenever the visible row set changes meaningfully.
  useEffect(() => {
    setSelectedIds(new Set());
    setBulkMessage(null);
  }, [query.page, query.sort, query.order, query.search, query.filters]);

  // ============================================================================
  // Load AAR options on mount
  // ============================================================================
  useEffect(() => {
    async function loadOptions() {
      try {
        setLoadingOptions(true);

        // Load recent AARs as picker options for the "new feedback" modal.
        const aarRes = await client
          .get('/canvass/aars', { params: { limit: 100 } })
          .catch(() => ({ data: [] }));

        const payload = aarRes.data;
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
          ? payload.items
          : [];

        const aars: AAROption[] = items.map((aar: any) => ({
          value: String(aar.id),
          label: `AAR #${aar.id} (Shift #${aar.canvass_shift_id}, ${
            aar.submitted_at || 'unknown date'
          })`,
        }));
        setAarOptions(aars);
      } catch (error) {
        console.error('Error loading AAR options:', error);
      } finally {
        setLoadingOptions(false);
      }
    }

    loadOptions();
  }, []);

  // ============================================================================
  // Pending-only toggle wiring (filters.applied)
  // ============================================================================
  const pendingOnly = String(query.filters.applied ?? '') === 'false';
  const togglePendingOnly = () => {
    query.setFilter('applied', pendingOnly ? undefined : 'false');
  };

  // ============================================================================
  // Bulk selection helpers
  // ============================================================================
  const visibleRowIds = useMemo(
    () => (query.items ?? []).map((r: AARFeedback) => r.id),
    [query.items]
  );
  const selectablePendingIds = useMemo(
    () =>
      (query.items ?? [])
        .filter((r: AARFeedback) => !r.applied_at)
        .map((r: AARFeedback) => r.id),
    [query.items]
  );
  const allPendingSelected =
    selectablePendingIds.length > 0 &&
    selectablePendingIds.every((id: number) => selectedIds.has(id));

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (allPendingSelected) {
        const next = new Set(prev);
        for (const id of selectablePendingIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of selectablePendingIds) next.add(id);
      return next;
    });
  };

  const handleBulkApply = async () => {
    if (selectedIds.size === 0 || bulkApplying) return;
    setBulkApplying(true);
    setBulkMessage(null);
    try {
      const result = await bulkApplyFeedback(Array.from(selectedIds));
      setBulkMessage({
        kind: 'success',
        text: `Marked ${result.updated_count} feedback row(s) as applied.`,
      });
      setSelectedIds(new Set());
      query.refetch();
    } catch (err: any) {
      setBulkMessage({
        kind: 'error',
        text:
          err?.response?.data?.detail ||
          err?.message ||
          'Bulk apply failed; please retry.',
      });
    } finally {
      setBulkApplying(false);
    }
  };

  // ============================================================================
  // Column definitions for DataTable
  // ============================================================================
  const columns = [
    {
      key: '_select',
      header: '',
      render: (row: AARFeedback) => {
        const disabled = !!row.applied_at;
        return (
          <input
            type="checkbox"
            aria-label={`Select feedback ${row.id}`}
            data-testid={`feedback-select-${row.id}`}
            disabled={disabled}
            checked={selectedIds.has(row.id)}
            onChange={(e) => {
              e.stopPropagation();
              toggleSelect(row.id);
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 accent-status-success-bg disabled:opacity-30 min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
          />
        );
      },
      className: 'w-10',
    },
    {
      key: 'feedback_tag',
      header: 'Tag',
      sortable: true,
      render: (row: AARFeedback) => {
        const colorClass = getFeedbackTagColor(row.feedback_tag);
        const label = getFeedbackTagLabel(row.feedback_tag);
        return (
          <span
            className={`${colorClass} px-3 py-1 rounded-md text-sm font-medium border`}
          >
            {label}
          </span>
        );
      },
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row: AARFeedback) => {
        if (!row.reason) return <span className="text-fg-muted">—</span>;
        const truncated =
          row.reason.length > 80
            ? `${row.reason.substring(0, 80)}…`
            : row.reason;
        return (
          <span title={row.reason} className="text-fg-muted text-sm cursor-help">
            {truncated}
          </span>
        );
      },
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (row: AARFeedback) => {
        const date = new Date(row.created_at);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      key: 'applied_at',
      header: 'Applied',
      sortable: true,
      render: (row: AARFeedback) => {
        if (!row.applied_at) {
          return (
            <span
              data-testid="feedback-status-pending"
              className="px-2 py-1 bg-status-warning-bg/30 border border-status-warning-bg text-status-warning rounded text-xs font-medium"
            >
              Pending
            </span>
          );
        }
        const date = new Date(row.applied_at);
        return (
          <span
            data-testid="feedback-status-applied"
            className="px-2 py-1 bg-status-success-bg/30 border border-status-success-bg text-status-success rounded text-xs font-medium"
          >
            {date.toLocaleString('en-US', {
              month: 'short',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        );
      },
    },
  ];

  // ============================================================================
  // Handle new-feedback form submission
  // ============================================================================
  const handleSubmitFeedback = async () => {
    if (!formAarId || !formTag) {
      setFormError('Please select an AAR and a feedback tag');
      return;
    }

    setFormSubmitting(true);
    setFormError(null);
    setFormSuccess(false);

    try {
      await submitAARFeedback(Number(formAarId), {
        feedback_tag: formTag,
        reason: formReason || undefined,
      });

      // Success
      setFormSuccess(true);
      setFormAarId(null);
      setFormTag(null);
      setFormReason('');

      // Close modal after short delay
      setTimeout(() => {
        setShowNewFeedbackModal(false);
        query.refetch();
      }, 1000);
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.message ||
        'Failed to submit feedback';
      setFormError(message);
    } finally {
      setFormSubmitting(false);
    }
  };

  // ============================================================================
  // Reset modal state on close
  // ============================================================================
  const handleCloseModal = () => {
    if (formSubmitting) return;
    setShowNewFeedbackModal(false);
    setFormAarId(null);
    setFormTag(null);
    setFormReason('');
    setFormError(null);
    setFormSuccess(false);
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
      <div className="max-w-full">
        {/* Explainer banner */}
        <div
          className="mb-6 p-4 bg-bg-raised/60 border border-border-base rounded-md"
          data-testid="feedback-loop-banner"
        >
          <p className="text-fg-secondary text-sm">
            <strong>Feedback loop.</strong> Tags <em>High Quality</em>,{' '}
            <em>Low Quality</em>, <em>Too Soft</em>, and <em>Great Insights</em>{' '}
            are auto-applied by the replay job (about once a minute). Rows
            stamped <span className="text-status-success">Applied</span> have been
            absorbed into the audit trail. <em>Mixed</em> stays{' '}
            <span className="text-status-warning">Pending</span> until a coordinator
            marks it applied. Select rows and use{' '}
            <strong>Mark selected as applied</strong> to stamp them now.
          </p>
        </div>

        {/* Header + New Feedback button */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">AAR Feedback</h1>
          <button
            onClick={() => setShowNewFeedbackModal(true)}
            className="px-4 py-2 bg-status-success-bg hover:bg-status-success-bg/80 rounded-md text-white font-medium transition-colors"
          >
            + New Feedback
          </button>
        </div>

        {/* FilterBar */}
        <FilterBar>
          <SearchInput
            placeholder="Search feedback reason"
            value={query.search || ''}
            onChange={(value) => query.setSearch(value)}
          />

          <FilterSelect
            label="Tag"
            options={FEEDBACK_TAG_OPTIONS}
            value={query.filters.feedback_tag ? String(query.filters.feedback_tag) : null}
            onChange={(value) => query.setFilter('feedback_tag', value || undefined)}
          />

          <FilterDateRange
            value={[
              query.filters.created_at__gte
                ? new Date(String(query.filters.created_at__gte))
                : null,
              query.filters.created_at__lte
                ? new Date(String(query.filters.created_at__lte))
                : null,
            ]}
            onChange={([from, to]) => {
              query.setFilter(
                'created_at__gte',
                from ? from.toISOString().split('T')[0] : undefined
              );
              query.setFilter(
                'created_at__lte',
                to ? to.toISOString().split('T')[0] : undefined
              );
            }}
          />
        </FilterBar>

        {/* Secondary control strip: pending toggle + bulk action */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-fg-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={pendingOnly}
              onChange={togglePendingOnly}
              className="h-4 w-4 accent-status-success-bg"
              data-testid="feedback-pending-toggle"
            />
            Pending only
          </label>

          {selectablePendingIds.length > 0 && (
            <button
              type="button"
              onClick={toggleSelectAllVisible}
              className="text-sm text-status-success hover:text-status-success/80 underline"
              data-testid="feedback-select-all-visible"
            >
              {allPendingSelected
                ? 'Deselect visible pending'
                : `Select visible pending (${selectablePendingIds.length})`}
            </button>
          )}

          <div className="flex-1" />

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-fg-secondary" data-testid="feedback-selected-count">
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                onClick={handleBulkApply}
                disabled={bulkApplying}
                className="px-4 py-2 bg-status-success-bg hover:bg-status-success-bg/80 disabled:opacity-60 rounded-md text-white text-sm font-medium transition-colors"
                data-testid="feedback-bulk-apply-btn"
              >
                {bulkApplying ? 'Marking…' : 'Mark selected as applied'}
              </button>
            </div>
          )}
        </div>

        {/* Bulk message */}
        {bulkMessage && (
          <div
            role="status"
            data-testid="feedback-bulk-message"
            className={`mb-4 p-3 rounded-md text-sm border ${
              bulkMessage.kind === 'success'
                ? 'bg-status-success-bg/30 border-status-success-bg text-status-success'
                : 'bg-status-danger-bg/30 border-status-danger-bg text-status-danger'
            }`}
          >
            {bulkMessage.text}
          </div>
        )}

        {/* Active filter chips */}
        {(Object.keys(query.filters).length > 0 || query.search) && (
          <div className="mb-4 flex flex-wrap gap-2">
            {query.search && (
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-status-success-bg/40 border border-status-success-bg text-status-success rounded-full text-sm">
                q: {query.search}
                <button
                  onClick={() => query.setSearch('')}
                  className="text-status-success hover:text-fg-primary"
                >
                  ×
                </button>
              </span>
            )}
            {Object.entries(query.filters).map(([key, value]) => {
              if (
                value === undefined ||
                value === null ||
                value === ''
              )
                return null;
              return (
                <span
                  key={key}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-status-success-bg/40 border border-status-success-bg text-status-success rounded-full text-sm"
                >
                  {key}: {String(value)}
                  <button
                    onClick={() => query.setFilter(key, undefined)}
                    className="text-status-success hover:text-fg-primary"
                  >
                    ×
                  </button>
                </span>
              );
            })}
            <button
              onClick={() => query.clearAll()}
              className="text-status-success hover:text-status-success/80 text-sm underline"
            >
              Clear all
            </button>
          </div>
        )}

        {/* DataTable (or EmptyState when truly empty with no active filters) */}
        {!query.loading &&
        query.items.length === 0 &&
        !query.search &&
        Object.keys(query.filters).length === 0 ? (
          <EmptyState
            icon={MessageCircle}
            title="No feedback yet"
            description="Feedback submitted through the Glossary and tooltips will appear here."
          />
        ) : (
          <DataTable<AARFeedback>
            columns={columns}
            query={query}
            rowKey={(row) => row.id}
            emptyMessage="No feedback entries yet. Start by creating new feedback."
            emptyFilteredMessage="No feedback entries match these filters."
          />
        )}
      </div>

      {/* ========================================================================
          New Feedback Modal
          ======================================================================== */}
      {showNewFeedbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-raised rounded-lg border border-border-base max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">New AAR Feedback</h2>
                <button
                  onClick={handleCloseModal}
                  disabled={formSubmitting}
                  className="text-fg-muted hover:text-fg-primary text-2xl disabled:opacity-50"
                >
                  ×
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4">
                {/* AAR Select */}
                <div>
                  <label className="block text-sm font-semibold text-fg-secondary mb-2">
                    After-Action Review
                  </label>
                  <select
                    value={formAarId || ''}
                    onChange={(e) => setFormAarId(e.target.value || null)}
                    disabled={loadingOptions || formSubmitting}
                    className="w-full px-3 py-2 bg-bg-elevated border border-border-subtle rounded-md text-fg-primary placeholder-fg-muted disabled:opacity-50"
                  >
                    <option value="">
                      {loadingOptions ? 'Loading AARs...' : 'Select an AAR'}
                    </option>
                    {aarOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Feedback Tag Select */}
                <div>
                  <label className="block text-sm font-semibold text-fg-secondary mb-2">
                    Feedback Tag <span className="text-accent-fg">*</span>
                  </label>
                  <select
                    value={formTag || ''}
                    onChange={(e) => setFormTag(e.target.value || null)}
                    disabled={formSubmitting}
                    className="w-full px-3 py-2 bg-bg-elevated border border-border-subtle rounded-md text-fg-primary placeholder-fg-muted disabled:opacity-50"
                  >
                    <option value="">Select a tag</option>
                    {FEEDBACK_TAG_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reason Textarea */}
                <div>
                  <label className="block text-sm font-semibold text-fg-secondary mb-2">
                    Reason (optional)
                  </label>
                  <textarea
                    value={formReason}
                    onChange={(e) => setFormReason(e.target.value)}
                    disabled={formSubmitting}
                    placeholder="E.g., 'Canvasser was too aggressive with older voters' or 'Excellent data quality on economy concerns'"
                    maxLength={2000}
                    className="w-full px-3 py-2 bg-bg-elevated border border-border-subtle rounded-md text-fg-primary placeholder-fg-muted disabled:opacity-50 h-24 resize-none"
                  />
                  <div className="mt-1 text-xs text-fg-muted">
                    {formReason.length}/2000
                  </div>
                </div>

                {/* Error message */}
                {formError && (
                  <div className="p-3 bg-status-danger-bg/30 border border-status-danger-bg text-status-danger rounded-md text-sm">
                    {formError}
                  </div>
                )}

                {/* Success message */}
                {formSuccess && (
                  <div className="p-3 bg-status-success-bg/30 border border-status-success-bg text-status-success rounded-md text-sm">
                    Feedback submitted successfully!
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={handleCloseModal}
                  disabled={formSubmitting}
                  className="px-4 py-2 bg-bg-elevated hover:bg-border-subtle text-fg-primary rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitFeedback}
                  disabled={formSubmitting || !formAarId || !formTag}
                  className="px-4 py-2 bg-status-success-bg hover:bg-status-success-bg/80 text-white rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  {formSubmitting ? 'Submitting…' : 'Submit Feedback'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
